/**
 * In-session memory for recently selected questions.
 * Backed by sessionStorage so selections survive page refreshes but are
 * automatically cleared when the tab or browser is closed.
 *
 * Scoped per dashboard+dataset pair. Max 5 entries per key (FIFO).
 * Cleared for a specific dashboard via clearDashboardMemory().
 *
 * Slot keys allow crosstab to track row/column selections separately.
 */

export type QuestionSlot = "general" | "row" | "column";

export interface QuestionMemoryEntry {
  questionId: string;
  questionType: string;
  datasetId: string;
  slot: QuestionSlot;
  timestamp: number;
}

const MAX_DEPTH = 5;
const SS_PREFIX = "qsm:"; // sessionStorage key prefix

// key format: `qsm:${dashboardId}:${datasetId}:${slot}`
function makeKey(dashboardId: string, datasetId: string, slot: QuestionSlot): string {
  return `${SS_PREFIX}${dashboardId}:${datasetId}:${slot}`;
}

function readList(key: string): QuestionMemoryEntry[] {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as QuestionMemoryEntry[]) : [];
  } catch {
    return [];
  }
}

function writeList(key: string, list: QuestionMemoryEntry[]): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(list));
  } catch {
    // sessionStorage quota exceeded or unavailable — fail silently
  }
}

export const questionSessionMemory = {
  push(
    dashboardId: string,
    entry: Omit<QuestionMemoryEntry, "timestamp">,
  ): void {
    // Guard: require all identifiers to be non-empty
    if (!dashboardId || !entry.questionId || !entry.datasetId) return;

    const key = makeKey(dashboardId, entry.datasetId, entry.slot);
    const list = readList(key);

    // Remove existing entry for the same question to avoid duplicates
    const deduped = list.filter((e) => e.questionId !== entry.questionId);

    // Prepend new entry, cap at MAX_DEPTH
    const updated = [{ ...entry, timestamp: Date.now() }, ...deduped].slice(
      0,
      MAX_DEPTH,
    );

    writeList(key, updated);
  },

  /**
   * Returns recent question entries in order (most recent first)
   * for the given dashboard+dataset+slot combination.
   */
  getRecent(
    dashboardId: string,
    datasetId: string,
    slot: QuestionSlot = "general",
  ): QuestionMemoryEntry[] {
    return readList(makeKey(dashboardId, datasetId, slot));
  },

  /**
   * Call this when the researcher navigates away from a dashboard
   * to remove all session entries scoped to that dashboard.
   */
  clearDashboardMemory(dashboardId: string): void {
    const prefix = `${SS_PREFIX}${dashboardId}:`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  },
};
